import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  CalendarIcon, 
  ClockIcon, 
  TagIcon,
  ChevronRightIcon,
  UserCircleIcon,
  ArrowLongLeftIcon
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BlogImagePlaceholder from '../components/common/BlogImagePlaceholder';
import Footer from '../components/common/Footer';
import SiteHeader from '../components/common/SiteHeader';

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  featuredImage?: {
    url: string;
    alt: string;
  };
  author?: {
    name: string;
    avatar?: string;
  };
  category?: string;
  tags?: string[];
  publishedAt?: string;
  readingTime?: number;
  views?: number;
  metaTitle?: string;
  metaDescription?: string;
}

const Blog: React.FC = () => {
  const { slug } = useParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Update meta tags for SEO
    if (slug && selectedPost) {
      document.title = selectedPost.metaTitle || `${selectedPost.title} | Twenty Four Capital Blog`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', selectedPost.metaDescription || selectedPost.excerpt);
      }
    } else {
      document.title = 'Blog & Resources | Twenty Four Capital';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Stay informed with insights and analysis on equity financing, market trends, and financial strategies from Twenty Four Capital.');
      }
    }
  }, [slug, selectedPost]);

  useEffect(() => {
    if (slug) {
      fetchSinglePost(slug);
      fetchPosts(); // Also fetch all posts to get the correct index
    } else {
      fetchPosts();
    }
  }, [slug]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/blog/posts?status=published');
      
      if (response.data && response.data.posts) {
        setPosts(response.data.posts || []);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setError('Failed to load blog posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSinglePost = async (postSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/blog/posts/${postSlug}`);
      
      if (response.data && response.data.post) {
        setSelectedPost(response.data.post);
        // Track view
        axios.put(`/api/blog/posts/${postSlug}/view`).catch(console.error);
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setError('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Date unknown';
    }
  };

  const getPostImage = (post: BlogPost, index: number = 0) => {
    if (post.featuredImage?.url) {
      return post.featuredImage.url;
    }
    // Use the same Picsum pattern as BlogCarousel but with unique seed per post
    const seed = post.category?.toLowerCase().replace(/\s+/g, '-') || 'finance';
    // Use the post slug or ID to ensure uniqueness
    const uniqueId = post.slug || post._id || `post-${index}`;
    return `https://picsum.photos/seed/${seed}-${uniqueId}/800/400`;
  };

  if (loading) {
    return (
      <>
        <SiteHeader />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <SiteHeader />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700">
              Return to Home
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Single Post View
  if (slug && selectedPost) {
    return (
      <>
        <SiteHeader />
        <article className="min-h-screen bg-white">
          {/* Hero Section with Featured Image */}
          <div className="relative h-96 overflow-hidden bg-gradient-to-br from-primary-100 to-primary-200">
            {/* Placeholder as base layer */}
            <BlogImagePlaceholder 
              category={selectedPost.category}
              className="w-full h-full"
            />
            
            {/* Actual image overlay */}
            <img 
              src={getPostImage(selectedPost)} 
              alt={selectedPost.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              onError={(e) => {
                // Hide the broken image to show placeholder
                e.currentTarget.style.display = 'none';
              }}
            />
            
            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            
            {/* Content */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="max-w-4xl mx-auto">
                <Link to="/blog" className="text-white/80 hover:text-white mb-4 inline-flex items-center">
                  <ArrowLongLeftIcon className="h-5 w-5 mr-2" />
                  Back to Blog
                </Link>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  {selectedPost.title}
                </h1>
                <div className="flex items-center space-x-6 text-white/80 text-sm">
                  <div className="flex items-center">
                    <UserCircleIcon className="h-5 w-5 mr-2" />
                    {selectedPost.author?.name || 'Twenty Four Capital Team'}
                  </div>
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2" />
                    {formatDate(selectedPost.publishedAt || new Date().toISOString())}
                  </div>
                  {selectedPost.readingTime && (
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 mr-2" />
                      {selectedPost.readingTime} min read
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Article Content */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="prose prose-lg prose-primary max-w-none">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({...props}) => <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
                  h2: ({...props}) => <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
                  h3: ({...props}) => <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" {...props} />,
                  p: ({...props}) => <p className="text-gray-700 leading-relaxed mb-6" {...props} />,
                  ul: ({...props}) => <ul className="list-disc list-inside mb-6 space-y-2" {...props} />,
                  ol: ({...props}) => <ol className="list-decimal list-inside mb-6 space-y-2" {...props} />,
                  li: ({...props}) => <li className="text-gray-700" {...props} />,
                  blockquote: ({...props}) => (
                    <blockquote className="border-l-4 border-primary-600 pl-6 py-2 my-6 italic text-gray-700" {...props} />
                  ),
                  strong: ({...props}) => <strong className="font-bold text-gray-900" {...props} />,
                  a: ({...props}) => <a className="text-primary-600 hover:text-primary-700 underline" {...props} />,
                }}
              >
                {selectedPost.content || selectedPost.excerpt}
              </ReactMarkdown>
            </div>

            {/* Tags */}
            {selectedPost.tags && selectedPost.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex items-center flex-wrap gap-2">
                  <TagIcon className="h-5 w-5 text-gray-400" />
                  {selectedPost.tags.map((tag, index) => (
                    <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
        <Footer />
      </>
    );
  }

  // Blog List View
  return (
    <>
      <SiteHeader />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1 
              className="text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Insights & Resources
            </motion.h1>
            <motion.p 
              className="text-xl text-white/90 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Expert analysis and educational content on equity financing, market trends, and strategic insights for public companies
            </motion.p>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {posts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No articles available at the moment.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post, index) => (
                  <motion.article
                    key={post._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
                  >
                    <Link to={`/blog/${post.slug}`} className="block relative h-full">
                      <div className="h-64 relative overflow-hidden">
                        <BlogImagePlaceholder 
                          category={post.category}
                          className="w-full h-full"
                        />
                        <img
                          src={getPostImage(post, index)}
                          alt={post.title || 'Blog post image'}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            // Hide the broken image to show the placeholder underneath
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        {/* Dark overlay gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                        
                        {/* Content overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                          {post.category && (
                            <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wide inline-block mb-3">
                              {post.category}
                            </span>
                          )}
                          <h2 className="text-xl font-bold mb-2 line-clamp-2 group-hover:text-orange-200 transition-colors">
                            {post.title}
                          </h2>
                          <div className="flex items-center text-sm text-gray-200">
                            <UserCircleIcon className="h-4 w-4 mr-1" />
                            <span className="mr-4">{post.author?.name || 'Twenty Four Capital'}</span>
                            {post.readingTime && (
                              <>
                                <ClockIcon className="h-4 w-4 mr-1" />
                                <span>{post.readingTime} min</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        {post.excerpt && (
                          <p className="text-gray-600 mb-4 line-clamp-3">
                            {post.excerpt}
                          </p>
                        )}

                        <span className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium group">
                          Read More 
                          <ChevronRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Blog; 